import ChainLogo from '@components/ChainLogo'
import useEvmChain from '@plug-core/hooks/useEVMChain'
import { ListGroup, ListRow, RowTile, Sheet } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiCheckCircle, HiPlus } from 'react-icons/hi'
import { useNavigate } from 'react-router-dom'

const ChainRow = ({
  chain,
  onSelectChain,
  selected,
}: {
  chain: string
  onSelectChain: (chain: string) => void
  selected: boolean
}) => {
  const { t } = useTranslation()
  const evmChain = useEvmChain(chain)

  return (
    <ListRow
      dense
      selected={selected}
      aria-current={selected || undefined}
      onClick={() => onSelectChain(chain)}
      leading={<ChainLogo chain={chain} size={32} />}
      primary={evmChain?.name || t(`chains.long.${chain}`)}
      trailing={
        selected ? <HiCheckCircle aria-hidden="true" className="size-5 text-success" /> : undefined
      }
    />
  )
}

interface ChainPickerProps {
  chains: string[]
  initialChain?: string
  chainSelectVisible: boolean
  setChainSelectVisible: (b: boolean) => void
  onChainChange: (newChain: string) => void
  addNewButton?: boolean
}

const ChainPicker: React.FC<ChainPickerProps> = ({
  chains,
  initialChain,
  chainSelectVisible,
  setChainSelectVisible,
  onChainChange,
  addNewButton,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [chainMode, setChainMode] = useState(initialChain)

  const onSelectChain = useCallback(
    (mode: string) => {
      setChainMode(mode)
      onChainChange(mode)
      setChainSelectVisible(false)
    },
    [onChainChange, setChainMode, setChainSelectVisible]
  )

  return (
    <Sheet
      open={chainSelectVisible}
      onClose={() => setChainSelectVisible(false)}
      title={t(`chains.select`)}
    >
      <ListGroup className="max-h-[60vh] shrink-0 overflow-y-auto no-scrollbar">
        {chains.map((chain, index) => (
          <ChainRow
            chain={chain}
            onSelectChain={() => onSelectChain(chain)}
            selected={chainMode === chain}
            key={index}
          />
        ))}

        {addNewButton && (
          <ListRow
            dense
            onClick={() => navigate(ROUTES.MANAGE_EVM)}
            leading={
              <RowTile className="size-8 text-[18px]">
                <HiPlus aria-hidden="true" />
              </RowTile>
            }
            primary={t('chains.addMore')}
          />
        )}
      </ListGroup>
    </Sheet>
  )
}

export default ChainPicker
